import React from "react";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import { Container } from "@mui/material";
import {
  MeterBar,
  MeterContainer,
  MeterFill,
  MeterLabel,
  RequirementItem,
  RequirementsList,
} from "./styles";
import { PasswordStrengthProps, Requirement } from "./types";

const requirements: Requirement[] = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "Contains uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "Contains lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "Contains number", test: (p) => /\d/.test(p) },
  {
    label: "Contains special character",
    test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p),
  },
];

const calculateStrength = (password: string): number => {
  if (!password) return 0;
  const metRequirements = requirements.filter((req) =>
    req.test(password),
  ).length;
  return (metRequirements / requirements.length) * 100;
};

const getStrengthLabel = (
  strength: number,
): { label: string; color: string } => {
  if (strength === 0) return { label: "", color: "" };
  if (strength < 40) return { label: "Weak", color: "var(--color-error)" };
  if (strength < 80) return { label: "Medium", color: "var(--color-warning)" };
  return { label: "Strong", color: "var(--color-success-6)" };
};

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({
  password,
}) => {
  const strength = calculateStrength(password);
  const { label, color } = getStrengthLabel(strength);

  if (!password) return null;

  return (
    <Container>
      <MeterContainer>
        <MeterLabel strength={strength} color={color}>
          Password Strength: {label}
        </MeterLabel>
        <MeterBar>
          <MeterFill strength={strength} color={color} />
        </MeterBar>
      </MeterContainer>

      <RequirementsList>
        {requirements.map((req) => {
          const isMet = req.test(password);
          return (
            <RequirementItem key={req.label} $isMet={isMet}>
              {isMet ? (
                <CheckCircleOutlined
                  style={{
                    fontSize: "var(--text-md)",
                    color: "var(--color-success-6)",
                  }}
                />
              ) : (
                <CancelOutlined
                  style={{
                    fontSize: "var(--text-md)",
                    color: "var(--color-text-secondary)",
                  }}
                />
              )}
              <span>{req.label}</span>
            </RequirementItem>
          );
        })}
      </RequirementsList>
    </Container>
  );
};
